import Vue from 'vue';
import VueRouter, { Route, RouteConfig } from 'vue-router';
import Auth from '@/modules/auth/pages/Auth.vue';
import store from '@/store';
import { MUTATIONS } from '@/store/mutations';
import { GETTERS } from '@/store/getters';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '/login',
    name: 'Login',
    component: Auth,
  },
  {
    path: '/',
    name: 'Home',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "home" */ '../modules/home/pages/Home.vue'),
  },
  {
    path: '/instant-booking',
    name: 'Legacy Booking',
    component: () => import(/* webpackChunkName: "home" */ '../modules/legacy/LegacyBookingRedirect.vue'),
  },
  {
    path: '/booking',
    name: 'Booking',
    component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/Booking.vue'),
    children: [
      {
        path: 'start-date',
        name: 'Booking Start Date',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingStartDate.vue'),
      },
      {
        path: 'start-time',
        name: 'Booking Start Time',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingStartTime.vue'),
      },
      {
        path: 'end-date',
        name: 'Booking End Date',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingEndDate.vue'),
      },
      {
        path: 'end-time',
        name: 'Booking End Time',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingEndTime.vue'),
      },
      {
        path: 'car-selection',
        name: 'Booking Car Selection',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingCarSelection.vue'),
      },
      {
        path: 'car-selection-details',
        name: 'Booking Car Selection Details',
        component: () =>
          import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingCarSelectionDetails.vue'),
      },
      {
        path: 'overview',
        name: 'Booking Overview',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingOverview.vue'),
      },
      {
        path: 'edit/:id',
        name: 'Booking Edit',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/BookingEdit.vue'),
      },
      {
        path: 'instant/:id',
        name: 'Instant Booking',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/InstantBooking.vue'),
      },
      {
        path: 'check-in/:id',
        name: 'Check-in Booking',
        component: () => import(/* webpackChunkName: "booking" */ '../modules/booking/pages/CheckInBooking.vue'),
      },
    ],
  },
  {
    path: '*',
    redirect: () => {
      // Redirect to the login route when none of the above matches.
      return '/login';
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

const isAuthenticated = (): boolean => {
  return store.getters[GETTERS.GET_IS_AUTHENTICATED];
};

function isHashCodeRoute(route: Route): boolean {
  return !!route.hash && !route.hash.includes('code=');
}

/** Removes the hash (#) to make the new routing system compatible with the old QR Codes. */
function uniformUrl(route: Route): string {
  if (isHashCodeRoute(route)) {
    return route.hash.slice(1);
  }
  return route.fullPath;
}

router.beforeEach((to, _from, next) => {
  if (to.name === 'Login') {
    if (isAuthenticated()) {
      return next({ path: '/' });
    }
  } else {
    if (!isAuthenticated()) {
      const url = uniformUrl(to);
      store.commit(MUTATIONS.SET_LAST_URL, url);
      return next({ path: '/login' });
    }
  }

  if (isHashCodeRoute(to)) {
    return next({ path: uniformUrl(to) });
  }
  return next();
});

export default router;
